import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "../../components/ui";
import { BuilderWorkspace } from "./WorkflowBuilderPage";
import { WorkflowDraftLibrary } from "./WorkflowDraftLibrary";
import { WorkflowDraftDetails } from "./WorkflowDraftDetails";
import { useBuilderIdentity } from "./useBuilderIdentity";
import styles from "./WorkflowBuilder.module.css";

type Props = { onClose: () => void };
type View = { kind: "library" } | { kind: "details"; draftId: string } | { kind: "editor"; draftId?: string };

export function WorkflowBuilderWindow({ onClose }: Props) {
    const { scope, switching } = useBuilderIdentity();
    return <LibraryWindow key={`${scope}:${switching}`} switching={switching} onClose={onClose} />;
}

function LibraryWindow({ switching, onClose }: Props & { switching: boolean }) {
    const { t } = useTranslation();
    const [view, setView] = useState<View>({ kind: "library" });
    return <Modal open size="fullscreen" className={styles.editorWindow} onOpenChange={open => { if (!open) onClose(); }}>
        <Dialog.Title className={styles.accessibleTitle}>{t("workflowBuilder.libraryTitle")}</Dialog.Title>
        <Dialog.Description className={styles.accessibleTitle}>{t("workflowBuilder.privateHelp")}</Dialog.Description>
        <Button className={styles.editorClose} variant="ghost" iconOnly aria-label={t("settingsPage.close")} onClick={onClose}><X size={20} /></Button>
        {view.kind === "library" ? <div className={styles.libraryWindowBody}>
            <WorkflowDraftLibrary onOpen={draftId => setView({ kind: "details", draftId })} onCreate={() => setView({ kind: "editor" })} />
        </div> : view.kind === "details" ? <div className={styles.detailsWindowBody}>
            <WorkflowDraftDetails key={view.draftId} draftId={view.draftId} switching={switching}
                onBack={() => setView({ kind: "library" })}
                onEdit={() => setView({ kind: "editor", draftId: view.draftId })} />
        </div> : <BuilderWorkspace key={view.draftId ?? "new"} draftId={view.draftId} switching={switching}
            onClose={() => setView(view.draftId ? { kind: "details", draftId: view.draftId } : { kind: "library" })}
            backLabel={t(view.draftId ? "workflowBuilder.backToWorkflow" : "workflowBuilder.backToLibrary")}
            onCreate={() => setView({ kind: "editor" })}
            onDraftCreated={draft => setView({ kind: "editor", draftId: draft.id })} />}
    </Modal>;
}
